<script setup lang="ts">
  import type { HTMLAttributes } from "vue";
  import { useVModel } from "@vueuse/core";
  import { useSlots, computed } from "vue";
  import { cn } from "@/lib/utils";

  defineOptions({ inheritAttrs: false });

  const props = defineProps<{
    class?: HTMLAttributes["class"];
    defaultValue?: string | number;
    modelValue?: string | number;
  }>();

  const emits = defineEmits<{
    "update:modelValue": [payload: string | number];
  }>();

  const modelValue = useVModel(props, "modelValue", emits, {
    passive: true,
    defaultValue: props.defaultValue,
  });

  const slots = useSlots();
  const hasWrapper = computed(() => !!slots.prepend || !!slots.append);
</script>

<template>
  <div
    v-if="hasWrapper"
    data-slot="textarea-wrapper"
    :class="
      cn(
        'border-input flex w-full items-start gap-2 rounded-md border bg-transparent px-3 py-2 shadow-xs transition-[color,box-shadow]',
        'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ring',
        'has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
        'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        props.class,
      )
    ">
    <slot name="prepend" />
    <textarea
      v-model="modelValue"
      data-slot="textarea"
      v-bind="$attrs"
      :class="
        cn(
          'placeholder:text-muted-foreground flex field-sizing-content min-h-0 w-full min-w-0 flex-1 bg-transparent text-13 outline-none disabled:pointer-events-none disabled:cursor-not-allowed',
        )
      " />
    <slot name="append" />
  </div>

  <textarea
    v-else
    v-model="modelValue"
    data-slot="textarea"
    v-bind="$attrs"
    :class="
      cn(
        'border-input placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-13 shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
        props.class,
      )
    " />
</template>
